import { Trans, useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'
import { useNavigate, useParams } from 'react-router-dom'
import { useDevices } from '../hooks/useDevices'
import { ID, Port, Strategy } from '../types/models'
import { PageText } from '../components/atoms/PageText'
import { Box, Dialog, DialogTitle, Stack, Typography, useTheme } from '@mui/material'
import { isDebug } from '../utils/misc'
import { Appliance, ApplianceConfig, ApplianceData, ApplianceSubtype, ApplianceType, SaveAbortedError } from '../classes/appliances/Appliance'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { Widget, WidgetData } from '../classes/widgets/Widget'
import { useEffect, useState } from 'react'
import { Text } from '../components/atoms/Text'
import { getAppliance } from '../classes/appliances'
import { PortControl } from '../classes/ports/PortControl'
import { PortConfig, UncommittedConfig } from '../types/ports'
import { getWidget } from '../classes/widgets'
import { BatteryAppliance } from '../classes/appliances/BatteryAppliance'
import { buildRelayedSubAppliance, RelayedKind } from '../classes/appliances/relayed-sub-appliance'
import { reportClientError } from '../utils/error-handler'
import { Button } from '../components/atoms/Button'
import { UnsavedChangesBanner } from '../components/atoms/UnsavedChangesBanner'
import { applianceEditState, useUnsavedChanges } from '../hooks/useUnsavedChanges'

// TODO: Split to smaller pieces.

export function ConfigureAppliancePage() {
  const { t } = useTranslation()
  const { type, subtype, portId, applianceId } = useParams()
  const { control } = useDevices()
  const app = useApp()
  const theme = useTheme()
  const navigate = useNavigate()
  const [appliance, setAppliance] = useState<Appliance>()
  const [appliances, setAppliances] = useState<Appliance[]>([])
  const [widgets, setWidgets] = useState<Widget[]>([])
  const [portControl, setPortControl] = useState<PortControl<Port, PortConfig>>()
  const [name, setName] = useState<string>('')
  const [deletable, setDeletable] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  const isNew = !applianceId

  // Initialize on first redraw.
  useEffect(() => {
    // Get the other appliances in order to have proper selector for some cases.
    for (const location of app.locations) {
      callAxios<ApplianceData[]>({
        url: `/appliances?location=${location.id}`,
      }).then(resp => {
        if (resp.success) {
          const apps = resp.data.map(appliance => getAppliance(appliance, control, app))
          apps.forEach(app => app.connectControls(control))
          setAppliances(apps)
        }
      })
    }

    if (isNew) {
      // Handle new appliance.
      // TODO: This is too long. Simplify.
      const portControl = control.findPortControl(parseInt(portId || '0') as ID)
      if (!portControl) {
        // The port in the URL is not among the loaded devices: a stale deep link, or a
        // history entry from before the session storage was cleared. Start over.
        app.addWarning(t('The selected port was not found. Please start again.'))
        navigate('/appliances/new', { replace: true })
        return
      }
      const portIds = portControl.getAssociatedPortIDs()
      const newAppliance = getAppliance({
        type: type as ApplianceType,
        name: portControl.getName(),
        location_id: portControl.deviceControl.device.location_id as ID,
        ports: portIds,
        config: Appliance.restoreConfig()
      }, control, app)
      newAppliance.initializeConfig(portControl, type as ApplianceType, subtype as ApplianceSubtype)
      portIds.forEach(id => {
        const ctrl = control.portControlForId(id)
        ctrl.setConfigDefaults()
        // Uh oh. Sometimes we need to force changes in order to get them into effect.
        if (ctrl.port.type === 'OPTIMIZER_CONTROL') {
          ctrl.setConfig('enabled', true)
          ctrl.setConfig('enabled', false) // No optimizer. Default is MANUAL.
        } else {
          ctrl.setConfig('enabled', false)
          ctrl.setConfig('enabled', true) // Other ports must be enabled.
        }
      })
      const config = newAppliance.getDefaultConfig()
      Appliance.storeConfig(config)
      Object.keys(config).forEach(k => newAppliance.setConfig(k, config[k as keyof ApplianceConfig]))
      setAppliance(newAppliance)
      setPortControl(portControl)
      setWidgets(newAppliance.getDefaultWidgets())
      setName(newAppliance.getName())
      newAppliance.connectControls(control)
      newAppliance.applyConfig()
      newAppliance.createHook()
    } else {
      // Handle existing appliance.
      callAxios<ApplianceData>({
        url: `/appliances/${applianceId}`,
      }).then(res => {
        if (res.success) {
          const oldAppliance = getAppliance(res.data, control, app)
          // Reset battery' sliders state
          if (oldAppliance?.type === 'BATTERY' || oldAppliance?.type === 'FCR_BATTERY') {
            oldAppliance.setConfig('has_han', false)
            oldAppliance.setConfig('has_solar', false)
          }
          const portControl = control.findPortControl(oldAppliance.portIDs[0])
          if (!portControl) {
            // The appliance points at a port of a device that is no longer ours.
            app.addWarning(t('The port of the appliance was not found. Please check your devices.'))
            navigate('/appliances', { replace: true })
            return
          }
          const portIds = portControl.getAssociatedPortIDs()
          portIds.forEach(id => {
            const ctrl = control.portControlForId(id)
            // Fix enabling in case it has got screwed.
            if (!ctrl.getConfig('enabled')) {
              const strategy = oldAppliance?.getConfig('strategy') as Strategy
              // A battery under manual command has its optimizer (0x220) off on
              // purpose. Without this the page would silently re-enable it and
              // the next save would flash the battery back out of manual mode.
              const manualBattery = ctrl.port.type === 'BATTERY_OPTIMIZER' && strategy === 'BATTERY_MANUAL'
              const manualRelay = ctrl.port.type === 'OPTIMIZER_CONTROL' && (strategy === 'MANUAL' || strategy === 'CALENDAR')
              if (!manualBattery && !manualRelay) {
                ctrl.setConfig('enabled', true)
              }
            }
          })
          Appliance.storeConfig(oldAppliance.config)
          setAppliance(oldAppliance)
          setPortControl(portControl)
          const newWidgets: Widget[] = []
          res.data.widgets?.forEach(w => {
            const add = getWidget(w, control, app)
            add.appliances.push(oldAppliance)
            newWidgets.push(add)
          })
          setWidgets(newWidgets)
          setName(oldAppliance.getName())
          oldAppliance.connectControls(control)
          oldAppliance.applyConfig()
          oldAppliance.createHook()
        } else {
          app.addError(res)
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Edits not saved yet. A new appliance is unsaved as a whole until Continue, so only existing ones get the note.
  const unsaved = useUnsavedChanges(appliance && portControl ? applianceEditState(appliance, control) : undefined)

  // Wait for appliance loading.
  if (!appliance || !portControl) return <></>

  // Handler for saving.
  const onDoneNew = async (): Promise<void> => {
    const batteryAppliance = (appliance.type === 'BATTERY' || appliance.type === 'FCR_BATTERY') && appliance as BatteryAppliance
    // Check if any HAN or Solar already existed in this device
    const hasHan = batteryAppliance && batteryAppliance.getConfig('has_han') as boolean
    const hasSolar = batteryAppliance && batteryAppliance.getConfig('has_solar') as boolean

    // Sync dual-state config (e.g. FCR Battery RS485 params) before reading localStorage.
    appliance.prepareSave()

    // Create appliances.
    const appData: ApplianceData = {
      location_id: portControl.deviceControl.device.location_id as ID,
      type: type as ApplianceType,
      ports: portControl.getAssociatedPortIDs(),
      name,
      config: Appliance.restoreConfig()
    }

    const applianceList: Appliance<{}, ApplianceConfig>[] = [appliance]
    const applianceDataList: ApplianceData[] = [appData]
    const widgetList: Widget[] = [...widgets]

    if (appliance.type === 'BATTERY' || appliance.type === 'FCR_BATTERY') {
      // Create the HAN / PV data-relaying sub-appliances for enabled toggles.
      // buildRelayedSubAppliance() returns null when nothing must be created —
      // the device already has a relayed appliance of that kind (creating it
      // again would duplicate it) or it has no free port left.
      const relayedKinds: RelayedKind[] = []
      if (hasHan) relayedKinds.push('EM_HAN')
      if (hasSolar) relayedKinds.push('SOLAR')
      for (const kind of relayedKinds) {
        const relayed = buildRelayedSubAppliance(kind, portControl, appliances, control, app)
        if (!relayed) continue
        applianceDataList.push(relayed.data)
        applianceList.push(relayed.subAppliance)
        widgetList.push(...relayed.subAppliance.getDefaultWidgets())
      }
    }
    const primaryWidgetIDList: ID[] = []
    const items: UncommittedConfig[] = []
    let index = 0
    for (const applianceData of applianceDataList) {
      const res = await callAxios<ApplianceData>({
        method: 'POST',
        url: '/appliances',
      }, applianceData)
      if (!res.success) {
        app.addError(res)
        return
      }

      // Build explicit flash payload instead of writing to port pending.
      items.push(...applianceList[index++].buildFlashPayload())

      const filteredWidgets = widgetList.filter(currentWidget => currentWidget.appliance.type === applianceData.type)

      // Create widgets for appliance.
      for (const w of filteredWidgets) {
        const widgetData: WidgetData = { ...w.toJSON(), appliances: [res.data.id] as ID[], location_id: appliance.location_id } as WidgetData
        const res2 = await callAxios<WidgetData>({
          method: 'POST',
          url: '/widgets',
        }, widgetData)
        if (!res2.success) {
          app.addError(res2)
          return
        }
        primaryWidgetIDList?.push(res2.data.id as ID)
      }
    }

    // Send hardware config for flashing.
    try {
      const multiOptItems = await Appliance.configureMultiOpt(control)
      items.push(...multiOptItems)
    } catch (e) {
      console.error('configureMultiOpt failed, flashing collected items anyway:', e)
    }
    await appliance.beforeFlash()
    await control.flashItems(items)
    control.clearPendingConfig()
    await appliance.afterSave()

    if (appliance.hasTestPage()) {
      navigate(`/appliances/test/${primaryWidgetIDList?.at(0)}`)
    } else {
      navigate('/')
    }
  }

  const onDoneOld = async (): Promise<void> => {
    const batteryAppliance = (appliance.type === 'BATTERY' || appliance.type === 'FCR_BATTERY') && appliance as BatteryAppliance
    const hasHan = batteryAppliance && batteryAppliance.getConfig('has_han') as boolean
    const hasSolar = batteryAppliance && batteryAppliance.getConfig('has_solar') as boolean

    const applianceList: Appliance<{}, ApplianceConfig>[] = []
    const applianceDataList: ApplianceData[] = []
    const widgetList: Widget[] = []
    const items: UncommittedConfig[] = []

    if (appliance.type === 'BATTERY' || appliance.type === 'FCR_BATTERY') {
      // Create the HAN / PV data-relaying sub-appliances for enabled toggles.
      // buildRelayedSubAppliance() returns null when nothing must be created —
      // the device already has a relayed appliance of that kind (creating it
      // again would duplicate it) or it has no free port left (used to crash
      // the whole save with nothing shown to the user).
      const relayedKinds: RelayedKind[] = []
      if (hasHan) relayedKinds.push('EM_HAN')
      if (hasSolar) relayedKinds.push('SOLAR')
      for (const kind of relayedKinds) {
        const relayed = buildRelayedSubAppliance(kind, portControl, appliances, control, app)
        if (!relayed) continue
        applianceDataList.push(relayed.data)
        applianceList.push(relayed.subAppliance)
        widgetList.push(...relayed.subAppliance.getDefaultWidgets())
      }

      const primaryWidgetIDList: ID[] = []
      let index = 0
      for (const applianceData of applianceDataList) {
        const res = await callAxios<ApplianceData>({
          method: 'POST',
          url: '/appliances',
        }, applianceData)

        if (!res.success) {
          app.addError(res)
          return
        }

        // Build explicit flash payload instead of writing to port pending.
        items.push(...applianceList[index++].buildFlashPayload())

        const filteredWidgets = widgetList.filter(currentWidget => currentWidget.appliance.type === applianceData.type)
        // Create widgets for appliance.
        for (const w of filteredWidgets) {
          const widgetData: WidgetData = { ...w.toJSON(), appliances: [res.data.id] as ID[], location_id: portControl.deviceControl.device.location_id as ID } as WidgetData
          const res2 = await callAxios<WidgetData>({
            method: 'POST',
            url: '/widgets',
          }, widgetData)
          if (!res2.success) {
            app.addError(res2)
            return
          }
          primaryWidgetIDList?.push(res2.data.id as ID)
        }
      }
    }

    // Save appliance to backend, then build explicit flash payload.
    await appliance.saveConfig()
    const payload = appliance.buildFlashPayload()
    if (appliance.flashOnlyWhenNeeded()) {
      // Leave out the ports the device is known to have exactly like this: the
      // flash would change nothing but its confmit would still reboot the device.
      // Anything unknown, unacknowledged or different is flashed as before.
      items.push(...payload.filter(item => !control.portControlForId(item.id).isConfirmedOnDevice(item.config as Record<string, unknown>)))
    } else {
      items.push(...payload)
    }

    // Collect optimizer changes and flash everything explicitly.
    try {
      const multiOptItems = await Appliance.configureMultiOpt(control)
      items.push(...multiOptItems)
    } catch (e) {
      console.error('configureMultiOpt failed, flashing collected items anyway:', e)
    }
    await appliance.beforeFlash()
    await control.flashItems(items)
    control.clearPendingConfig()
    await appliance.afterSave()

    if (appliance.alwaysTestPage()) {
      const primaryWidgetID = widgets.filter(w => w.type === 'COMSUMPTION_STAT')[0].id
      navigate(`/appliances/test/${primaryWidgetID}`)
    } else {
      navigate('/appliances')
    }
  }

  // Save button. An appliance may demand an extra confirmation for a change
  // with consequences beyond this device (leaving the reserve market); the
  // save then runs only after the user accepts it.
  const onSaveClicked = (): void => {
    const text = appliance.saveConfirmation()
    if (text) {
      setConfirmation(text)
    } else {
      onDone()
    }
  }

  const onDone = async (): Promise<void> => {
    if (saving) return
    setConfirmation(null)
    setSaving(true)
    try {
      if (isNew) {
        await onDoneNew()
      } else {
        await onDoneOld()
      }
    } catch (e) {
      console.error('Appliance save failed:', e)
      // A SaveAbortedError comes from a guard (e.g. FCRBatteryAppliance.
      // buildFlashPayload) that has already shown the error to the user and
      // reported it to Slack. Anything else is an unexpected crash mid-save —
      // without this the button would silently do nothing.
      if (!(e instanceof SaveAbortedError)) {
        reportClientError(`Appliance save failed: ${e}`, e instanceof Error ? e.stack || '' : '')
        app.addError(t('Saving failed unexpectedly. Please reload the page and try again.'))
      }
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async (): Promise<void> => {
    control.clearPendingConfig()

    const res = await callAxios<ApplianceData>({
      method: 'DELETE',
      url: `/appliances/${appliance.id}`,
    })
    if (!res.success) {
      app.addError(res)
      return
    }

    // The server switches the appliance's ports off on the device as part of
    // the delete (ApplianceController.delete). Flashing them from here as
    // well would race the server's own configure/confmit cycle — and the
    // client-side batch used to include ports the server refuses (0x222,
    // 0x223), which lost the whole flash and left the ports enabled. Only the
    // multi-opt role changes are still resolved client-side.
    await control.flashItems(control.configureMultiOptRoles())
    control.clearPendingConfig()

    setDeletable(false)
    navigate('/appliances')
  }

  const top = appliance.configTop(appliances)
  const topInfo = appliance.infoTop()
  const topLeft = appliance.configTopLeft()
  const topRight = appliance.configTopRight()
  const bottomLeft = appliance.configBottomLeft()
  const bottomRight = appliance.configBottomRight()
  const bottom = appliance.configBottom(appliances)
  const cannotAddBattery = portControl.port.port_number === '22/1' && appliance.type === 'FCR_BATTERY'

  return <>
    <TopTitle title={t('Configure {name}').replace('{name}', appliance.getName())}></TopTitle>
    {!isNew && unsaved && <UnsavedChangesBanner saveButtonId="appliance-save"/>}
    <PageText><Trans>Enion Device</Trans>: {portControl.deviceControl.device.hw_id}</PageText>
    <PageText><Trans>Port</Trans>: {portControl.getName()} {isDebug('port-numbers') ? `[${portControl.port.port_number} #${portControl.port.id}]` : ''}</PageText>
    <PageText><Trans>Appliance Type</Trans>: {appliance.getType()}</PageText>
    <Box sx={{
      mt: 2,
      borderRadius: '0.625rem',
      backgroundColor: theme.palette.configBackground.main,
      boxShadow: theme.boxShadow.main,
      padding: '0.5rem',
    }}>
      {cannotAddBattery && <>
        <Typography>
          <Trans>Energy storage port is not available. Ensure that old non-reserve market battery has been removed first. Please contact us if needed.</Trans>
        </Typography>
      </>}

      {!cannotAddBattery && <>
      <Text
        margin="normal"
        fullWidth
        data-cy="text-Config appliance's name"
        label={app.t('Name')}
        name="model"
        size="small"
        autoComplete="name"
        value={name}
        placeholder={appliance.getName()}
        onChange={(e) => { setName(e.target.value); appliance.name = e.target.value }}
        sx={{ px: 1, mb: 2 }}
      />
      <Box sx={{ mx: 1 }}>
        {
          /* Appliance configuration */
          top
        }
      </Box>
      { topInfo &&
        <Box sx={{ mx: 1, mb: 2 }}>
        {
          /* Port info */
          topInfo
        }
        </Box>
      }
      <Box sx={{ m: '0.5rem', display: 'grid', gridTemplateColumns: '50% 50%' }}>
        { /* Hardware configuration top left section */
          ((topLeft || topRight) && app.installerLevel) &&
            <Box sx={{ mb: 2 }}>
              {app.installerLevel ? topLeft : (appliance.type === 'EV_CHARGER' ? <></> : topLeft)}
            </Box>
        }
        { /* Hardware configuration top right section */
          ((topLeft || topRight) && app.installerLevel) &&
            <>
               {app.installerLevel ? <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem' }}>{topRight}</Box> : (appliance.type === 'EV_CHARGER' ? <></> : <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem' }}>{topRight}</Box>)}
            </>
        }
        { /* Hardware configuration bottom left section */
          (bottomLeft || bottomRight) &&
            <Box sx={{ mb: 2 }}>
              {bottomLeft}
            </Box>
        }
        { /* Hardware configuration bottom right section */
          (bottomLeft || bottomRight) &&
            <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem' }}>
              {bottomRight}
            </Box>
        }
      </Box>
      {
        bottom &&
        <Box sx={{ mx: 1 }}>
          {
            /* Additional appliance configuration */
            bottom
          }
        </Box>
      }
      <Box sx={{ m: '0.5rem', display: 'grid', gridTemplateColumns: '50% 50%' }}>

        { isDebug('port-json') && <Box><hr/></Box> }
        { isDebug('port-json') && <Box><hr/></Box> }
        { isDebug('port-json') && <Box>
            <Typography>Appliance Config</Typography>
            <pre style={{ fontSize: '80%', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(appliance.config, null, 2)}
            </pre>
            <Typography>HW Config</Typography>
            {
              appliance.controls.map((control, idx) => <pre key={idx} style={{ fontSize: '80%', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                <b>{control.port.port_number}</b><br/>
                {JSON.stringify(control.port.config, null, 2)}
              </pre>)
            }
          </Box>
        }
        { isDebug('port-json') && <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem', minHeight: '4rem' }}>
            <Typography>Values</Typography>
            <pre style={{ fontSize: '80%' }}>
              {JSON.stringify(portControl.port.values, null, 2)}
            </pre>
            <Typography>Flags</Typography>
            {JSON.stringify(portControl.port.flags)}
          </Box>
        }
      </Box>
      </>}

    </Box>
    <Box sx={{ mt: '2rem', textAlign: 'center' }}>
      <Button id="appliance-save" fullWidth onClick={onSaveClicked} data-cy="button-Continue" disabled={!appliance.canContinue()}>
        {isNew || appliance.alwaysTestPage() ? <Trans>Continue</Trans> : <Trans>Save</Trans>}
      </Button>
    </Box>
    {
      app.installerLevel && (!isNew &&
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button fullWidth sx={{ backgroundColor: 'error.dark' }} onClick={() => setDeletable(true)} data-cy="button-Delete"><Trans>Delete</Trans></Button>
        </Box>)
    }

    <Dialog open={!!confirmation} onClose={() => setConfirmation(null)}>
      <DialogTitle sx={{ backgroundColor: 'error.light' }}><Trans>Confirm</Trans></DialogTitle>
      <Box sx={{ p: 2 }} data-cy="box-Save confirmation">
        <Typography>{confirmation}</Typography>
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setConfirmation(null)}><Trans>Cancel</Trans></Button>
          <Button color="secondary" onClick={onDone}><Trans>Yes</Trans></Button>
        </Stack>
      </Box>
    </Dialog>

    {app.installerLevel && <Dialog open={!!deletable} onClose={() => setDeletable(false)}>
      <DialogTitle sx={{ backgroundColor: 'error.light' }}><Trans>Delete Appliance?</Trans></DialogTitle>
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">{appliance.getName()}</Typography>
        <Trans>Are you sure that you want to delete appliance?</Trans>
        <Stack direction="row" spacing={2} sx={{ m: 2 }}>
          <Button color="primary" onClick={() => setDeletable(false)}><Trans>Cancel</Trans></Button>
          <Button color="secondary" onClick={onDelete}><Trans>Yes</Trans></Button>
        </Stack>
      </Box>
    </Dialog>}
  </>
}
